import { fn, hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Card from 'tio-reimagined/components/card';
import Component from '@glimmer/component';
import { eq } from 'tio-ui/utilities';
import SectionHeader from 'tio-reimagined/components/section-header';
import Text from 'tio-reimagined/components/text';
import type AnswerProfileModel from 'tio-common/models/answer-profile';
import type { IntlService } from 'ember-intl';
import type PersonModel from 'tio-common/models/person';
import type RecommendationModel from 'tio-common/models/recommendation';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import {
  PhArrowCircleRight,
  PhCaretDown,
  PhCaretUp,
  PhCheckCircle,
  PhLightbulb,
} from 'ember-phosphor-icons';

export interface PslfRecommendationsSignature {
  Args: {
    loanSummary: LoanSummary;
    person: PersonModel;
    recommendation?: RecommendationModel;
    debugLinkLoans?: boolean;
    answerProfile?: AnswerProfileModel;
  };
  Element: HTMLDivElement;
}

interface RecommendationItem {
  id: string;
  titleKey: string;
  descriptionKey: string;
}

export default class PslfRecommendations extends Component<PslfRecommendationsSignature> {
  @service declare intl: IntlService;

  @tracked expandedId: string | null = null;

  get answerProfile() {
    return this.args.answerProfile;
  }

  get loanSummariesByType() {
    return this.args.loanSummary?.summaryByLoanType || [];
  }

  get shouldConsolidateLoans() {
    return !!this.loanSummariesByType.find((summary) => {
      return summary.loanType !== 'direct';
    });
  }

  get hasMissingRepaymentPlan() {
    const allRepaymentPlans = this.loanSummariesByType.flatMap((summary) => summary.repaymentPlans);
    return allRepaymentPlans.some((plan) => plan === 'noRepaymentPlan' || !plan);
  }

  get shouldChangeRepaymentPlan() {
    if (this.hasMissingRepaymentPlan || this.loanSummariesByType.length === 0) {
      return false;
    }
    const validRepaymentPlansForPslf = ['icr', 'ibr', 'paye', 'repaye', 'fivePercentUndergrad'];
    const allRepaymentPlans = [
      ...new Set(this.loanSummariesByType.flatMap((summary) => summary.repaymentPlans)),
    ];
    return (
      allRepaymentPlans.length > 0 &&
      allRepaymentPlans.every((plan) => !validRepaymentPlansForPslf.includes(plan))
    );
  }

  get hasNsldsData() {
    return (this.args.person.accounts ?? []).some((account) => account.hasNsldsPslfData);
  }

  get shouldLinkLoans() {
    if (this.args.debugLinkLoans) return true;
    return !this.hasNsldsData;
  }

  get needsToRecertify() {
    return !!this.args.recommendation;
  }

  // Whether external recs are checked off by the user
  get isConsolidationChecked() {
    return !!this.answerProfile?.consolidatedLoansAt;
  }

  get isRepaymentPlanChecked() {
    return !!this.answerProfile?.changedRepaymentPlanAt;
  }

  get isRecertifyIncomeChecked() {
    return !!this.answerProfile?.recertifiedIncomeAt;
  }

  isCheckedOff = (id: string): boolean => {
    if (id === 'consolidate') return this.isConsolidationChecked;
    if (id === 'changeRepaymentPlan') return this.isRepaymentPlanChecked;
    if (id === 'recertifyIncome') return this.isRecertifyIncomeChecked;
    return false;
  };

  get allRecommendations(): RecommendationItem[] {
    const items: RecommendationItem[] = [];

    if (this.shouldConsolidateLoans) {
      items.push({
        id: 'consolidate',
        titleKey: 'pslf.reimagined.recommendations.consolidate_loans.title',
        descriptionKey: 'pslf.reimagined.recommendations.consolidate_loans.description',
      });
    }

    if (this.hasMissingRepaymentPlan) {
      items.push({
        id: 'provideRepaymentPlan',
        titleKey: 'pslf.reimagined.recommendations.provide_repayment_plan.title',
        descriptionKey: 'pslf.reimagined.recommendations.provide_repayment_plan.description',
      });
    }

    if (this.shouldChangeRepaymentPlan) {
      items.push({
        id: 'changeRepaymentPlan',
        titleKey: 'pslf.reimagined.recommendations.change_repayment_plan.title',
        descriptionKey: 'pslf.reimagined.recommendations.change_repayment_plan.description',
      });
    }

    if (this.shouldLinkLoans) {
      items.push({
        id: 'linkLoans',
        titleKey: 'pslf.reimagined.recommendations.link_loans.title',
        descriptionKey: 'pslf.reimagined.recommendations.link_loans.description',
      });
    }

    if (this.needsToRecertify) {
      items.push({
        id: 'recertifyIncome',
        titleKey: 'pslf.reimagined.recommendations.certify_income.title',
        descriptionKey: 'pslf.reimagined.recommendations.certify_income.description',
      });
    }

    return items;
  }

  get activeRecommendationCount() {
    return this.allRecommendations.filter((rec) => !this.isCheckedOff(rec.id)).length;
  }

  get hasNoRecommendations() {
    return this.allRecommendations.length === 0;
  }

  get headingWithCount() {
    return `${this.intl.t('pslf.reimagined.recommendations.heading')} (${this.activeRecommendationCount})`;
  }

  toggleItem = (id: string) => {
    this.expandedId = this.expandedId === id ? null : id;
  };

  toggleConsolidation = async () => {
    const profile = this.answerProfile;
    if (!profile) return;
    try {
      profile.consolidatedLoansAt = profile.consolidatedLoansAt ? '' : new Date().toISOString();
      await profile.save();
    } catch (e) {
      console.error(e);
      profile.rollbackAttributes();
    }
  };

  toggleRepaymentPlan = async () => {
    const profile = this.answerProfile;
    if (!profile) return;
    try {
      profile.changedRepaymentPlanAt = profile.changedRepaymentPlanAt
        ? ''
        : new Date().toISOString();
      await profile.save();
    } catch (e) {
      console.error(e);
      profile.rollbackAttributes();
    }
  };

  toggleRecertifyIncome = async () => {
    const profile = this.answerProfile;
    if (!profile) return;
    try {
      profile.recertifiedIncomeAt = profile.recertifiedIncomeAt ? '' : new Date().toISOString();
      await profile.save();
    } catch (e) {
      console.error(e);
      profile.rollbackAttributes();
    }
  };

  <template>
    {{#unless this.hasNoRecommendations}}
      <Card @padding="lg" @roundedXl={{true}} data-test-pslf-recommendations ...attributes>
        <SectionHeader
          @icon={{PhLightbulb}}
          @title={{this.headingWithCount}}
          @subtitle={{t "pslf.reimagined.recommendations.subtitle"}}
          @iconClass="text-amber-500"
        />

        <div class="space-y-2">
          {{#each this.allRecommendations as |rec|}}
            <div
              data-test-recommendation-item={{rec.id}}
              class="border rounded-lg overflow-hidden
                {{if
                  (this.isCheckedOff rec.id)
                  'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20'
                  'border-gray-200 dark:border-gray-700'
                }}"
            >
              <button
                type="button"
                aria-expanded={{if (eq this.expandedId rec.id) "true" "false"}}
                class="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                data-test-pslf-recommendation-toggle={{rec.id}}
                {{on "click" (fn this.toggleItem rec.id)}}
              >
                <div
                  class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                    {{if
                      (this.isCheckedOff rec.id)
                      'bg-emerald-100 dark:bg-emerald-900'
                      'bg-gray-100 dark:bg-gray-800'
                    }}"
                >
                  {{#if (this.isCheckedOff rec.id)}}
                    <PhCheckCircle
                      @size={{16}}
                      @weight="fill"
                      class="text-emerald-600 dark:text-emerald-400"
                    />
                  {{else}}
                    <PhArrowCircleRight @size={{16}} class="text-gray-600 dark:text-gray-400" />
                  {{/if}}
                </div>
                <div class="flex-1 min-w-0">
                  <Text
                    @weight="semibold"
                    class="text-sm {{if (this.isCheckedOff rec.id) 'line-through text-gray-400'}}"
                  >
                    {{t rec.titleKey}}
                  </Text>
                  <Text @variant="muted" @size="sm" class="truncate">
                    {{t rec.descriptionKey}}
                  </Text>
                </div>
                <div class="shrink-0">
                  {{#if (eq this.expandedId rec.id)}}
                    <PhCaretUp @size={{16}} class="text-gray-400" />
                  {{else}}
                    <PhCaretDown @size={{16}} class="text-gray-400" />
                  {{/if}}
                </div>
              </button>

              {{#if (eq this.expandedId rec.id)}}
                <div class="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700">
                  {{! -- In-platform recs: link to NSLDS upload -- }}
                  {{#if (eq rec.id "provideRepaymentPlan")}}
                    <Text @variant="muted" @size="sm" class="mb-3">
                      {{t "recommendations.provide_repayment_plan_description"}}
                    </Text>
                    <LinkTo
                      @route="authenticated.observability.upload"
                      @query={{hash source="pslf" providers="nslds,statement"}}
                      class="inline-flex items-center gap-1.5 text-ocean-600 hover:text-ocean-700 text-sm font-medium"
                      data-test-pslf-upload-nslds="provideRepaymentPlan"
                    >
                      {{t "pslf.reimagined.recommendations.upload_nslds"}}
                      <PhArrowCircleRight @size={{14}} />
                    </LinkTo>
                  {{/if}}

                  {{#if (eq rec.id "linkLoans")}}
                    <Text @variant="muted" @size="sm" class="mb-3">
                      {{t "recommendations.please_add_accounts"}}
                    </Text>
                    <LinkTo
                      @route="authenticated.observability.upload"
                      @query={{hash source="pslf" providers="nslds,statement"}}
                      class="inline-flex items-center gap-1.5 text-ocean-600 hover:text-ocean-700 text-sm font-medium"
                      data-test-pslf-upload-nslds="linkLoans"
                    >
                      {{t "pslf.reimagined.recommendations.upload_nslds"}}
                      <PhArrowCircleRight @size={{14}} />
                    </LinkTo>
                  {{/if}}

                  {{! -- External recs: description + check-off toggle -- }}
                  {{#if (eq rec.id "consolidate")}}
                    <Text @variant="muted" @size="sm">
                      {{t "recommendations.required"}}
                    </Text>
                    <a
                      href="https://studentaid.gov/app/launchConsolidation.action"
                      target="_blank"
                      rel="noreferrer noopener"
                      class="inline-flex items-center gap-1.5 text-ocean-600 hover:text-ocean-700 text-sm font-medium mt-2"
                    >
                      {{t "pslf.reimagined.recommendations.consolidate_loans.link"}}
                      <PhArrowCircleRight @size={{14}} />
                    </a>
                    <div class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <button
                        type="button"
                        class="inline-flex items-center gap-2 text-sm font-medium transition-colors
                          {{if
                            this.isConsolidationChecked
                            'text-emerald-600 hover:text-emerald-700'
                            'text-gray-500 hover:text-gray-700'
                          }}"
                        data-test-pslf-mark-complete="consolidation"
                        {{on "click" this.toggleConsolidation}}
                      >
                        {{#if this.isConsolidationChecked}}
                          <PhCheckCircle @size={{16}} @weight="fill" class="text-emerald-600" />
                          {{t "pslf.reimagined.recommendations.mark_incomplete"}}
                        {{else}}
                          <PhCheckCircle @size={{16}} class="text-gray-400" />
                          {{t "pslf.reimagined.recommendations.mark_complete"}}
                        {{/if}}
                      </button>
                    </div>
                  {{/if}}

                  {{#if (eq rec.id "changeRepaymentPlan")}}
                    <Text @variant="muted" @size="sm">
                      {{t "recommendations.change_repayment_plan_pslf"}}
                    </Text>
                    <a
                      href="https://studentaid.gov/manage-loans/repayment/plans"
                      target="_blank"
                      rel="noreferrer noopener"
                      class="inline-flex items-center gap-1.5 text-ocean-600 hover:text-ocean-700 text-sm font-medium mt-2"
                    >
                      {{t "pslf.reimagined.recommendations.change_repayment_plan.link"}}
                      <PhArrowCircleRight @size={{14}} />
                    </a>
                    <div class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <button
                        type="button"
                        class="inline-flex items-center gap-2 text-sm font-medium transition-colors
                          {{if
                            this.isRepaymentPlanChecked
                            'text-emerald-600 hover:text-emerald-700'
                            'text-gray-500 hover:text-gray-700'
                          }}"
                        data-test-pslf-mark-complete="repayment-plan"
                        {{on "click" this.toggleRepaymentPlan}}
                      >
                        {{#if this.isRepaymentPlanChecked}}
                          <PhCheckCircle @size={{16}} @weight="fill" class="text-emerald-600" />
                          {{t "pslf.reimagined.recommendations.mark_incomplete"}}
                        {{else}}
                          <PhCheckCircle @size={{16}} class="text-gray-400" />
                          {{t "pslf.reimagined.recommendations.mark_complete"}}
                        {{/if}}
                      </button>
                    </div>
                  {{/if}}

                  {{#if (eq rec.id "recertifyIncome")}}
                    <Text @variant="muted" @size="sm">
                      {{t "recommendations.need_recertify_income"}}
                    </Text>
                    <a
                      href="https://studentaid.gov/idr/"
                      target="_blank"
                      rel="noreferrer noopener"
                      class="inline-flex items-center gap-1.5 text-ocean-600 hover:text-ocean-700 text-sm font-medium mt-2"
                    >
                      {{t "recommendations.recertify_income"}}
                      <PhArrowCircleRight @size={{14}} />
                    </a>
                    <div class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <button
                        type="button"
                        class="inline-flex items-center gap-2 text-sm font-medium transition-colors
                          {{if
                            this.isRecertifyIncomeChecked
                            'text-emerald-600 hover:text-emerald-700'
                            'text-gray-500 hover:text-gray-700'
                          }}"
                        data-test-pslf-mark-complete="recertify-income"
                        {{on "click" this.toggleRecertifyIncome}}
                      >
                        {{#if this.isRecertifyIncomeChecked}}
                          <PhCheckCircle @size={{16}} @weight="fill" class="text-emerald-600" />
                          {{t "pslf.reimagined.recommendations.mark_incomplete"}}
                        {{else}}
                          <PhCheckCircle @size={{16}} class="text-gray-400" />
                          {{t "pslf.reimagined.recommendations.mark_complete"}}
                        {{/if}}
                      </button>
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/each}}
        </div>
      </Card>
    {{/unless}}
  </template>
}
